import React, { useState, useEffect, useRef, useCallback } from 'react';

const SPIFFSUpload = () => {
  // State management
  const [port, setPort] = useState(null);
  const [writer, setWriter] = useState(null);
  const [reader, setReader] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [baudRate, setBaudRate] = useState('115200');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState('');
  const [availableImages, setAvailableImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [chunkSize, setChunkSize] = useState(1024);
  const [bufferTimer, setBufferTimer] = useState(50);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [logs, setLogs] = useState([]);

  // Refs
  const responseBufferRef = useRef('');
  const responseCallbackRef = useRef(null);

  // Logging function
  const log = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      message,
      type
    };
    setLogs(prev => [...prev, logEntry]);
  }, []);

  const clearLog = useCallback(() => {
    setLogs([]);
  }, []);

  // API functions
  const fetchImagesFromAPI = useCallback(async () => {
    setIsLoadingImages(true);
    try {
      const response = await fetch(
        'https://api.dqr-display-tms.bonrix.in/api/Share/DQR/FirstImage/JyfUGzIuF4-rh8i5egt-EuXv-FQjv-CdS8uUxJTu75-SPHGnzFp',
        {
          method: 'GET',
          headers: {
            'accept': '*/*'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      log('API data fetched successfully', 'success');
      
      // Check if response has the required image URLs
      if (!data.success || !data.fail || !data.qr || !data.pending || !data.welcome) {
        throw new Error('Invalid API response: Missing required image URLs');
      }
      
      // Map the API response properties to custom names
      const imageMapping = [
        { url: data.success, name: 'success.jpeg' },
        { url: data.fail, name: 'fail.jpeg' },
        { url: data.qr, name: 'qrcode.jpeg' },
        { url: data.pending, name: 'pending.jpeg' },
        { url: data.welcome, name: 'welcome.jpeg' }
      ];
      
      const images = [];
      
      // Fetch each image from its URL
      for (const imageInfo of imageMapping) {
        try {
          log(`Fetching image: ${imageInfo.name} from ${imageInfo.url}`, 'info');
          
          const imageResponse = await fetch(imageInfo.url);
          if (!imageResponse.ok) {
            throw new Error(`Failed to fetch ${imageInfo.name}: ${imageResponse.status}`);
          }
          
          const imageBlob = await imageResponse.blob();
          
          // Create object URL for display
          const objectURL = URL.createObjectURL(imageBlob);
          
          images.push({
            name: imageInfo.name,
            url: imageInfo.url,
            blob: imageBlob,
            objectURL: objectURL,
            size: imageBlob.size
          });
          
          log(`Successfully loaded ${imageInfo.name} (${imageBlob.size.toLocaleString()} bytes)`, 'success');
          
        } catch (error) {
          log(`Failed to load ${imageInfo.name}: ${error.message}`, 'error');
        }
      }
      
      setAvailableImages(images);
      log(`Loaded ${images.length} images from API`, 'success');
      
    } catch (error) {
      log(`Failed to fetch images: ${error.message}`, 'error');
      setUploadStatus(`Failed to load images: ${error.message}`);
    } finally {
      setIsLoadingImages(false);
    }
  }, [log]);

  // Serial connection functions
  const connectSerial = useCallback(async () => {
    try {
      const selectedPort = await navigator.serial.requestPort();
      await selectedPort.open({ baudRate: parseInt(baudRate, 10) });
      const portWriter = selectedPort.writable.getWriter();

      setPort(selectedPort);
      setWriter(portWriter);
      setIsConnected(true);

      log(`Serial port connected at ${baudRate} baud`, 'success');
      
      // Start reading incoming data
      readLoop(selectedPort);
    } catch (e) {
      log('Connection error: ' + e.message, 'error');
    }
  }, [baudRate, log]);

  const disconnectSerial = useCallback(async () => {
    try {
      if (writer) {
        writer.releaseLock();
        setWriter(null);
      }
      if (reader) {
        reader.releaseLock();
        setReader(null);
      }
      if (port) {
        await port.close();
        setPort(null);
      }

      setIsConnected(false);
      log('Serial port disconnected', 'success');
    } catch (e) {
      log('Disconnection error: ' + e.message, 'error');
    }
  }, [writer, reader, port, log]);

  // Read loop for incoming data
  const readLoop = useCallback(async (serialPort) => {
    if (!serialPort) return;

    try {
      const portReader = serialPort.readable.getReader();
      setReader(portReader);
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await portReader.read();
        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        responseBufferRef.current += chunk;

        // Trigger callback if waiting for response
        if (responseCallbackRef.current) {
          responseCallbackRef.current();
          responseCallbackRef.current = null;
        }

        // Process complete lines
        let lines = responseBufferRef.current.split('\n');
        responseBufferRef.current = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            log(`Received: ${trimmedLine}`, 'info');
          }
        }
      }
    } catch (error) {
      log(`Read loop error: ${error.message}`, 'error');
    } finally {
      if (reader) {
        reader.releaseLock();
        setReader(null);
      }
    }
  }, [log]);

  // Wait for response with timeout
  const waitForResponse = useCallback((timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        responseCallbackRef.current = null;
        reject(new Error('Timeout waiting for response'));
      }, timeout);

      responseCallbackRef.current = () => {
        clearTimeout(timeoutId);
        resolve();
      };
    });
  }, []);

  // Image selection
  const handleImageSelection = useCallback((imageName) => {
    const selectedImage = availableImages.find(img => img.name === imageName);
    if (selectedImage && selectedImage.blob) {
      // Create a File object from the blob
      const file = new File([selectedImage.blob], imageName, { type: 'image/jpeg' });
      setSelectedFile(file);
      setSelectedImageName(imageName);
      setUploadStatus(`Selected ${imageName} (${selectedImage.size.toLocaleString()} bytes)`);
      log(`Selected image: ${imageName}`, 'info');
    } else {
      setUploadStatus(`Failed to select ${imageName}`);
      log(`Image ${imageName} not available`, 'error');
    }
  }, [availableImages, log]);

  // Validation functions
  const validateChunkSize = useCallback((value) => {
    const size = parseInt(value);
    if (size < 256) return 256;
    if (size > 4096) return 4096;
    return size;
  }, []);

  const validateBufferTimer = useCallback((value) => {
    const timer = parseInt(value);
    if (timer < 10) return 10;
    if (timer > 1000) return 1000;
    return timer;
  }, []);

  // Upload single file function
  const uploadSingleFile = useCallback(async (file, fileName) => {
    if (!port || !writer) {
      throw new Error('Serial port not connected');
    }

    const validChunkSize = validateChunkSize(chunkSize);
    const validBufferTimer = validateBufferTimer(bufferTimer);

    const encoder = new TextEncoder();
    const startCommand = `sendingdefaultimage**${fileName}**${file.size}**${validChunkSize}`;
    await writer.write(encoder.encode(startCommand + '\n'));

    log(`Upload start command: ${startCommand}`, 'info');

    // Wait a moment for initial response
    await new Promise(resolve => setTimeout(resolve, 100));

    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const totalChunks = Math.ceil(file.size / validChunkSize);

    log(`Starting upload: ${totalChunks} chunks of ${validChunkSize} bytes each`, 'info');

    for (let i = 0; i < totalChunks; i++) {
      const start = i * validChunkSize;
      const end = Math.min(start + validChunkSize, file.size);
      const chunk = uint8Array.slice(start, end);

      await writer.write(chunk);
      log(`Sent chunk ${i + 1}/${totalChunks} (${chunk.length} bytes)`, 'info');

      // Wait for response with shorter timeout
      try {
        await waitForResponse(1000);
      } catch (error) {
        log(`No response for chunk ${i + 1}, continuing...`, 'warning');
      }

      // Update progress
      const progress = Math.round(((i + 1) / totalChunks) * 100);
      setUploadProgress(progress);

      // Buffer timer delay
      if (validBufferTimer > 0) {
        await new Promise(resolve => setTimeout(resolve, validBufferTimer));
      }
    }

    log(`SPIFFS upload completed successfully for ${fileName}`, 'success');
  }, [port, writer, chunkSize, bufferTimer, validateChunkSize, validateBufferTimer, waitForResponse, log]);

  // Upload function for single selected file
  const startUpload = useCallback(async () => {
    if (!selectedFile) {
      log('No file selected', 'error');
      setUploadStatus('Please select a JPEG file');
      return;
    }

    if (!port || !writer) {
      log('Serial port not connected', 'error');
      setUploadStatus('Please connect serial port first');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('Initializing upload...');

      await uploadSingleFile(selectedFile, selectedFile.name);

      setUploadStatus('Upload complete!');

    } catch (error) {
      log(`Upload failed: ${error.message}`, 'error');
      setUploadStatus(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);
    }
  }, [selectedFile, port, writer, uploadSingleFile, log]);

  // Upload all images function
  const startAllUpload = useCallback(async () => {
    if (availableImages.length === 0) {
      log('No images available to upload', 'error');
      setUploadStatus('Please load images first');
      return;
    }

    if (!port || !writer) {
      log('Serial port not connected', 'error');
      setUploadStatus('Please connect serial port first');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      for (let imageIndex = 0; imageIndex < availableImages.length; imageIndex++) {
        const image = availableImages[imageIndex];
        const file = new File([image.blob], image.name, { type: 'image/jpeg' });
        
        setUploadStatus(`Uploading ${image.name} (${imageIndex + 1}/${availableImages.length})...`);
        log(`Starting upload ${imageIndex + 1}/${availableImages.length}: ${image.name}`, 'info');

        await uploadSingleFile(file, image.name);

        // Update overall progress
        const overallProgress = Math.round(((imageIndex + 1) / availableImages.length) * 100);
        setUploadProgress(overallProgress);

        // Small delay between uploads
        if (imageIndex < availableImages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setUploadStatus(`All uploads complete! (${availableImages.length} images)`);
      log(`All ${availableImages.length} images uploaded successfully`, 'success');

    } catch (error) {
      log(`All upload failed: ${error.message}`, 'error');
      setUploadStatus(`All upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);
    }
  }, [availableImages, port, writer, uploadSingleFile, log]);

  // Cleanup function to revoke object URLs
  useEffect(() => {
    return () => {
      // Clean up object URLs to prevent memory leaks
      availableImages.forEach(image => {
        if (image.objectURL) {
          URL.revokeObjectURL(image.objectURL);
        }
      });
    };
  }, [availableImages]);

  // Initialize on component mount
  useEffect(() => {
    log('SPIFFS JPEG Upload Interface loaded', 'success');
    fetchImagesFromAPI();
  }, [log, fetchImagesFromAPI]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        disconnectSerial();
      }
    };
  }, [isConnected, disconnectSerial]);

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-600';
      case 'success': return 'text-green-600';
      case 'warning': return 'text-orange-600';
      default: return 'text-gray-800';
    }
  };

  return (
    <div className="flex gap-5 p-5 font-sans text-gray-800 min-h-screen bg-gray-50">
      <div className="flex-1 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-700 mb-6">SPIFFS JPEG Upload</h1>

        {/* Connection Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Serial Connection</h2>
          
          <div className={`p-3 rounded-lg mb-4 text-center font-semibold ${
            isConnected 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="baudRate" className="font-medium">Select Baud Rate:</label>
            <select 
              id="baudRate"
              value={baudRate}
              onChange={(e) => setBaudRate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="115200">115200 baud</option>
              <option value="230400">230400 baud</option>
              <option value="250000">250000 baud</option>
              <option value="460800">460800 baud</option>
              <option value="500000">500000 baud</option>
              <option value="921600">921600 baud</option>
              <option value="1000000">1000000 baud</option>
              <option value="2000000">2000000 baud</option>
            </select>
            
            <button 
              onClick={connectSerial}
              disabled={isConnected}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Connect
            </button>
            
            <button 
              onClick={disconnectSerial}
              disabled={!isConnected}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Disconnect
            </button>
          </div>
        </div>

        {/* SPIFFS Upload Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">SPIFFS JPEG Upload</h2>
          
          <div className="space-y-4">
            {/* Settings */}
            <div className="flex gap-5 flex-wrap">
              <div className="flex flex-col gap-1">
                <label htmlFor="chunkSize" className="font-medium text-sm">Chunk Size (bytes):</label>
                <input 
                  type="number" 
                  id="chunkSize"
                  value={chunkSize}
                  onChange={(e) => setChunkSize(validateChunkSize(e.target.value))}
                  min="256" 
                  max="4096"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex flex-col gap-1">
                <label htmlFor="bufferTimer" className="font-medium text-sm">Buffer Timer (ms):</label>
                <input 
                  type="number" 
                  id="bufferTimer"
                  value={bufferTimer}
                  onChange={(e) => setBufferTimer(validateBufferTimer(e.target.value))}
                  min="10" 
                  max="1000"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Image Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button 
                  onClick={fetchImagesFromAPI}
                  disabled={isLoadingImages}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoadingImages ? 'Loading Images...' : 'Refresh Images'}
                </button>
                
                <button 
                  onClick={startUpload}
                  disabled={!selectedFile || !isConnected || isUploading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors min-w-[150px]"
                >
                  {isUploading ? 'Uploading...' : 'Upload Selected'}
                </button>

                <button 
                  onClick={startAllUpload}
                  disabled={availableImages.length === 0 || !isConnected || isUploading}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors min-w-[150px]"
                >
                  {isUploading ? 'Uploading All...' : 'Upload All Images'}
                </button>
              </div>

              {/* Available Images */}
              {availableImages.length > 0 && (
  <div className="space-y-2">
    <label className="font-medium text-sm">Select Image:</label>
    <div className="grid grid-cols-2 gap-3 max-w-2xl">
      {availableImages.map((image) => (
        <button
          key={image.name}
          onClick={() => handleImageSelection(image.name)}
          className={`p-3 border rounded-lg transition-all hover:shadow-md flex items-center gap-3 text-left w-full ${
            selectedImageName === image.name
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          {/* Thumbnail */}
          <img
            src={image.objectURL}
            alt={image.name}
            className="h-24 w-16 object-cover rounded-md border border-gray-200 flex-shrink-0"
            onError={(e) => {
              console.error(`Failed to load image: ${image.name}`);
              e.target.src =
                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEwIDEwSDhWMTJIMTBWMTBaIiBmaWxsPSIjOTk5Ii8+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNCAyQzIuOSAyIDIgMi45IDIgNFYyMEMyIDIxLjEgMi45IDIyIDQgMjJIMjBDMjEuMSAyMiAyMiAyMS4xIDIyIDIwVjRDMjIgMi45IDIxLjEgMiAyMCAySDRaTTggMTRIMTZMMTMgMTBMMTEgMTNMOSAxMUw4IDE0WiIgZmlsbD0iIzk5OSIvPgo8L3N2Zz4K';
            }}
          />

          {/* Text info */}
          <div className="flex flex-col overflow-hidden">
            <div className="font-medium text-sm truncate">{image.name}</div>
            <div className="text-xs text-gray-500">
              {image.size.toLocaleString()} bytes
            </div>
            <div className="text-xs text-gray-400 truncate">
              {image.url.split('/').pop()}
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
)}


              {isLoadingImages && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center gap-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    Loading images from API...
                  </div>
                </div>
              )}

              {!isLoadingImages && availableImages.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-500">No images available. Click "Refresh Images" to try again.</div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {uploadProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-lg h-6">
                <div 
                  className="bg-green-500 h-6 rounded-lg transition-all duration-300 flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${uploadProgress}%` }}
                >
                  {uploadProgress}%
                </div>
              </div>
            )}

            {/* Status */}
            {uploadStatus && (
              <div className="font-medium text-sm text-gray-700 bg-gray-50 p-3 rounded-md border">
                {uploadStatus}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Log Section */}
      <div className="w-80 sticky top-5 self-start">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Log Output</h2>
        
        <div className="bg-gray-900 border border-gray-300 rounded-md p-3 h-96 overflow-y-auto font-mono text-xs whitespace-pre-wrap">
          {logs.length === 0 ? (
            <div className="text-gray-400">Log output will appear here...</div>
          ) : (
            logs.map((logEntry) => (
              <div key={logEntry.id} className={getLogColor(logEntry.type === 'info' ? 'text-gray-300' : logEntry.type)}>
                <span className="text-gray-500">[{logEntry.timestamp}]</span> <span className="text-blue-400">[{logEntry.type.toUpperCase()}]</span> <span className={logEntry.type === 'error' ? 'text-red-400' : logEntry.type === 'success' ? 'text-green-400' : logEntry.type === 'warning' ? 'text-yellow-400' : 'text-gray-300'}>{logEntry.message}</span>
              </div>
            ))
          )}
        </div>
        
        <button 
          onClick={clearLog}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full"
        >
          Clear Log
        </button>
      </div>
    </div>
  );
};

export default SPIFFSUpload;